import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CustomButton, CustomTable } from '../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { Headers, TableBody } from '../MBHReport/tableInfo';
import { ReportFilters, downloadFile } from '../../helpers';
import { REPORT_GENERATION_MESSAGE } from '../../../../shared/Helpers/constants';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import {
	GetBillingProperties,
	GetTotalBillingProperties,
} from '../../../../redux/API/ClientAPIHelpers/billingProperties';
import {
	downloadNCExcelReport,
	downloadTotalReport,
	getNonCredentialedSessions,
} from '../../../../redux/State/clientSlice/billingSlice';

const NonCredentialedReport = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const [params] = useSearchParams();

	const { clientIds, endDate, startDate, userId, onlyNew, timeError } = useSelector(
		(state) => state.billing.billingFilters,
	);
	const sessions = useSelector((state) => state.billing.ncSessions);
	const loading = useSelector((state) => state.billing.loading);

	const fetchData = useCallback(() => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';

		if (timeError) return;
		if (!startDate || !endDate) return;

		const data: GetBillingProperties = {
			page,
			pageSize,
			startDate,
			endDate,
			onlyNew,
			direction,
			orderBy,
			searchString,
			userId,
			clientIds,
		};

		dispatch(getNonCredentialedSessions(data)).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch, params, clientIds, endDate, startDate, userId, onlyNew, timeError]);

	const downloadNonCredentialedReportHandler = useCallback(() => {
		const page = params.get('page') || '1';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';

		const pageSize = sessions.totalNumberOfItems ? sessions.totalNumberOfItems.toString() : '8';

		if (!startDate || !endDate) return;

		toast(REPORT_GENERATION_MESSAGE);

		const data: GetBillingProperties = {
			page,
			pageSize,
			startDate,
			endDate,
			onlyNew,
			direction,
			orderBy,
			searchString,
			userId,
			clientIds,
		};
		dispatch(downloadNCExcelReport(data))
			.then(unwrapResult)
			.then((response) => {
				downloadFile(response);
				fetchData();
			})
			.catch(errorNormalizer);
	}, [dispatch, params, sessions, clientIds, endDate, startDate, userId, onlyNew, fetchData]);

	const downloadTotalReportHandler = useCallback(() => {
		if (!startDate || !endDate) return;

		toast(REPORT_GENERATION_MESSAGE);

		const data: GetTotalBillingProperties = userId
			? {
					startDate,
					endDate,
					userId,
					clientIds,
				}
			: {
					startDate,
					endDate,
					clientIds,
				};

		dispatch(downloadTotalReport(data))
			.then(unwrapResult)
			.then((response) => downloadFile(response))
			.catch(errorNormalizer);
	}, [dispatch, clientIds, endDate, startDate, userId]);

	useEffect(() => {
		const timeout = setTimeout(fetchData, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		return () => clearTimeout(timeout);
	}, [fetchData]);

	return (
		<>
			<ReportFilters showUsers isMultipleSelect />
			<div
				style={{
					display: 'flex',
					gap: '16px',
					alignItems: 'center',
					marginBottom: '16px',
				}}
			>
				<CustomButton
					onClick={downloadNonCredentialedReportHandler}
					title="Download Detailed Report"
					loading={loading}
					disabled={timeError}
				/>
				<CustomButton
					onClick={downloadTotalReportHandler}
					title="Download Total Report"
					loading={loading}
					disabled={timeError}
				/>
			</div>
			<CustomTable headers={Headers} TableBody={TableBody} data={sessions} loading={loading} hasMobile={false} />
		</>
	);
};

export default NonCredentialedReport;
